import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Tenant, ListFilter, Invite, Role } from '@parashift/shared/models';
import { ErrorResponse, TenantService, InviteService, RoleService, SessionService, NotificationService, Logger, JsonApiQueryData, PermissionService } from '@parashift/shared/services';
import { Permissions } from '@parashift/shared/constants';
import { PdcDatePipe } from '@parashift/shared/pipes';
import { DateFromNowDirective, CheckPermissionsDirective } from '@parashift/shared/directives';
import { DefaultDatesComponent, DeleteTenantModalComponent, ModalService } from '@parashift/shared/components';
import { InviteUsersModalComponent } from './invite-users-modal/invite-users-modal.component';
import { EditPermissionsModalComponent } from './edit-permissions-modal/edit-permissions-modal.component';
import { SubTenantsComponent } from './sub-tenants/sub-tenants.component';
import { SuperTenantComponent } from './super-tenant/super-tenant.component';
import { TenantSettingsBrandThemeComponent } from './brand-theme/tenant-settings-brand-theme.component';
import { UrlIntegrationComponent } from './url-integration/url-integration.component';
import { HeaderActionMenuItem, HeaderActionStore } from '@parashift/shared/stores';
import { PdcAuthenticationPolicyComponent } from './authentication-policy/pdc-authentication-policy.component';

@Component({
  selector: 'pdc-tenant-settings',
  templateUrl: './pdc-tenant-settings.component.html',
  styleUrls: ['./pdc-tenant-settings.component.scss'],
  imports: [
    CheckPermissionsDirective,
    DateFromNowDirective,
    DefaultDatesComponent,
    FaIconComponent,
    PdcAuthenticationPolicyComponent,
    PdcDatePipe,
    SubTenantsComponent,
    SuperTenantComponent,
    TenantSettingsBrandThemeComponent,
    TooltipModule,
    UrlIntegrationComponent
]
})
export class PdcTenantSettingsComponent implements OnInit {
  private tenantService = inject(TenantService);
  private inviteService = inject(InviteService);
  private roleService = inject(RoleService);
  private sessionService = inject(SessionService);
  private permissionService = inject(PermissionService);
  private headerActionStore = inject(HeaderActionStore);
  private modalService = inject(ModalService);
  private notificationService = inject(NotificationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private logger = inject(Logger);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  tenant: Tenant;
  tenant_id: number;
  currentTab = 'general';
  currentUserId: number;
  error: ErrorResponse;
  listFilter = new ListFilter({ include: 'roles,roles.user' });
  invites: Invite[];
  permissions = Permissions;
  editorPermissions = [];

  private role_id: string;

  constructor() {
    const destroyRef = this.destroyRef;

    destroyRef.onDestroy(() => this.headerActionStore.destroy());
  }

  ngOnInit() {
    this.addHeaderButtons();
    this.currentUserId = Number(this.sessionService.user_id);
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: ParamMap) => {
      this.tenant_id = params.get('tenant_id') ? Number(params.get('tenant_id')) : undefined;
      this.getTenant(this.tenant_id);
      this.getInvites();
    });

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: ParamMap) => {
      this.role_id = params.get('role') || undefined;
      this.currentTab = params.get('tab') || 'general';
    });
  }

  switchTab(tab: string) {
    this.router.navigate([], { relativeTo: this.route, queryParams: { tab }, queryParamsHandling: 'merge' });
  }

  private getTenant(id: number) {
    this.tenantService.findRecord(String(id), this.listFilter).subscribe({
      next: (tenant: Tenant) => {
        this.tenant = tenant;
        this.setEditorPermissions();
        this.openEditRolePermissionsModalOnInit();
      },
      error: (error: ErrorResponse) => {
        this.error = error;
        this.logger.log('tenant-settings-component', 'TENANT Error', error);
      }
    });
  }

  private setEditorPermissions() {
    const userRole = this.tenant.roles.find(role => role.user_id === this.currentUserId);

    if (userRole) {
      this.editorPermissions = userRole.permissions;
    }
  }

  private getInvites() {
    const listFilter = new ListFilter({
      filter: {
        tenant_id: this.tenant_id,
        accepted_at_blank: true,
        rejected_at_blank: true
      },
      page: { size: 1000 }
    });

    this.inviteService.findAll(listFilter).subscribe({
      next: (invites: JsonApiQueryData<Invite>) => this.invites = invites.getModels(),
      error: (error: ErrorResponse) => this.logger.log('tenant-settings-component', 'INVITES Error', error)
    });
  }

  private openEditRolePermissionsModalOnInit() {
    if (this.role_id) {
      const editRole = this.tenant.getRolesWithUsers().find(role => role.id === this.role_id);
      editRole && this.openEditRolePermissionsModal(editRole);
      this.role_id = undefined;
    }
  }

  checkEditorsPermission(permissions: Permissions[]): boolean {
    if (
      permissions.includes('owner' as Permissions)
      && !this.sessionService.userIsAdminSupportOrSales
      && !this.editorPermissions.includes('owner')
    ) {
      return false;
    }

    return true;
  }

  openInviteUserModal() {
    const model = {
      tenant_permissions: this.tenant.permissions
    };

    this.modalService.customized({ initialState: { model, data: { editorPermissions: this.editorPermissions } }}, InviteUsersModalComponent)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(result => {
      const invite = new Invite({
        tenant_id: this.tenant.id,
        permissions: result.permissions,
        user_groups: result.user_groups,
        email: String(result.email).toLowerCase()
      });
      this.saveInvite(invite);
    });
  }

  goToManageNotifications() {
    this.router.navigate([
      this.router.createUrlTree([this.sessionService.user.language, 'pdc', this.tenant_id, 'administration', 'notifications']).toString()
    ]);
  }

  openEditInvitePermissionsModal(invite: Invite) {
    const model = {
      tenant_permissions: this.tenant.permissions,
      user_groups: invite.user_groups,
      user_permissions: invite.permissions
    };

    this.modalService.customized({ initialState: { model, data: { editorPermissions: this.editorPermissions } }}, EditPermissionsModalComponent)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(result => {
      invite.permissions = result.user_permissions;
      invite.user_groups = result.user_groups;
      this.saveInvite(invite);
    });
  }

  openEditRolePermissionsModal(role: Role) {
    const model = {
      tenant_permissions: this.tenant.permissions,
      user_groups: role.user_groups,
      user_permissions: role.permissions,
      user: role.user
    };

    this.modalService.customized({ initialState: { model, data: { editorPermissions: this.editorPermissions } }}, EditPermissionsModalComponent)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(result => {
      role.permissions = result.user_permissions;
      role.user_groups = result.user_groups;
      this.saveRole(role);
    });
  }

  openRemoveUserModal(role: Role) {
    this.modalService.confirmation({
      initialState: {
        title: $localize `:@@components.tenant_settings.remove_user:Remove User`,
        description: $localize `:@@components.tenant_settings.remove_user_hint:Do you want to remove the user from this tenant?`
      }
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => {
      this.roleService.deleteRecord(role.id).subscribe({
        next: () => {
          this.getTenant(this.tenant_id);
          this.notificationService.success({
            description: $localize `:@@components.tenant_settings.remove_user_success:The user <strong>${role.user.name}:userName:</strong> has been successfully removed from this tenant`,
            title: $localize `:@@common.successfully_deleted:Successfully deleted`,
          });
        },
        error: (error: ErrorResponse) => this.notificationService.formattedErrorResponse(error)
      });
    });
  }

  openComplianceModal() {
    this.modalService.info({
      initialState: {
        title: $localize `:@@components.tenant_settings.gdpr_compliance:GDPR-Compliance`,
        description: $localize `:@@components.tenant_settings.gdpr_compliance_hint:The strictest confidentiality and protection against unauthorized access to customer data are of paramount importance to Parashift. All data transmitted to and processed by Parashift is protected in accordance with the latest IT security standards. Parashift takes all necessary steps to protect customer data from unauthorized access and ensures at all times during order processing that your data is only stored or processed in accordance with European data protection regulations. All employees are also obliged to comply with our internal data protection guidelines.<br><br>Parashift processes personal data on behalf of the customer within the meaning of Art. 4 No. 8 and Art. 28 of Regulation (EU) 2016/679 – Basic Data Protection Regulation (DSGVO).<br>The Order Data Agreement regulates the rights and obligations of the parties in connection with the processing of personal data.<br><br>You can download the latest template of this Order Data Agreement here:<br><br><a href=\"https://parashift.io/gdpr/\" target=\"_blank\" class=\"text-link\">https://parashift.io/gdpr/</a><br><br>We will be pleased to assist you in the finalisation of order data processing.`,
      }
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe();
  }

  openDeleteTenantModal() {
    this.modalService.customized({ initialState: { model: this.tenant }}, DeleteTenantModalComponent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  saveRole(role: Role) {
    const newRole = new Role({ id: role.id });
    newRole.permissions = role.permissions;
    newRole.user_groups = role.user_groups;
    this.roleService.saveRecord(newRole).subscribe({
      next: () => {
        this.notificationService.success({
          description: $localize `:@@components.tenant_settings.permission_save_success:The user permissions have been saved.`,
          title: $localize `:@@common.successfully_saved:Successfully saved`
        });
      },
      error: error => {
        this.notificationService.formattedErrorResponse(error);
        this.logger.log('SAVE ROLE ERROR', error);
      }
    });
  }

  saveInvite(invite: Invite) {
    invite.tenant_id = this.tenant.id;
    this.inviteService.saveRecord(invite).subscribe({
      next: () => {
        this.notificationService.success({
          description: $localize `:@@components.tenant_settings.invitation_sent:The invitation has been sent.`,
          title: $localize `:@@components.tenant_settings.successful_invitation:Successful Invitation`
        });
      },
      error: error => {
        this.notificationService.formattedErrorResponse(error);
        this.logger.log('SEND INVITATION ERROR', error);
      }
    });
  }

  getProductPermissions(permissions) {
    const productPermissions = [];

    permissions.forEach(key => {
      if (key.includes('pdc') || key.includes('admin') || key.includes('owner')) {
        productPermissions.push(key);
      }
    });

    return productPermissions;
  }

  resendInviteNotification(invite_id) {
    this.inviteService.resendInviteNotification(invite_id).subscribe({
      next: () => {
        this.notificationService.success({
          description: $localize `:@@components.tenant_settings.link_has_been_resent:The link has been resent`,
          title: $localize `:@@common.done!:Done!`
        });
      },
      error: error => {
        this.notificationService.formattedErrorResponse(error);
        this.logger.log('RESEND INVITATION LINK ERROR', error);
      }
    });
  }

  cancelInvitation(invite_id) {
    this.modalService.confirmation({
      initialState: {
        title: $localize `:@@components.tenant_settings.cancel_invitation_title:Cancel user invitation`,
        description: $localize `:@@components.tenant_settings.cancel_invitation_description:Do you want to cancel user invitation?`
      }
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => {
      this.inviteService.cancelInvitation(invite_id).subscribe({
        next: () => {
          this.invites = this.invites.filter(x => x.id !== invite_id);
          this.notificationService.success({
            description: $localize `:@@components.tenant_settings.invitation_cancelled:User invitation cancelled.`,
            title: $localize `:@@common.done!:Done!`
          });
        },
        error: error => {
          this.notificationService.formattedErrorResponse(error);
          this.logger.log('CANCEL INVITATION ERROR', error);
        }
      });
    });
  }

  editTenant(id) {
    this.router.navigate([id, 'edit'], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }

  private addHeaderButtons() {
    const menuItems: HeaderActionMenuItem[] = [
      {
        dispatch: () => this.tenant && !this.error && this.openDeleteTenantModal(),
        identifier: 'delete'
      },
      { identifier: 'divider', position: 100 },
      {
        dispatch: () => this.openComplianceModal(),
        identifier: 'gdprButton',
        position: 101,
        label: $localize `:@@components.pdc_tenant_settings.gdpr_compliance:GDPR Compliance`,
        cy: 'link-header-dropdown-gdpr'
      }
    ];

    if (this.permissionService.isRolesOrPermissionsAllowed({ roles: ['admin', 'support'], permissions: ['admin', 'owner'] })) {
      menuItems.push({
        dispatch: () => this.tenant && !this.error && this.goToManageNotifications(),
        identifier: 'manageNotificationsButton',
        position: 102,
        label: $localize `:@@components.pdc_tenant_settings.manage_notifications:Manage Notifications`,
        cy: 'link-header-dropdown-manage-notifications'
      });
    }

    this.headerActionStore.addMenuItems(menuItems);
  }
}
